// Which kind of page a web address is, for search engines: pages to index (the
// landing page, the Store, guides, about, support, terms, refunds, privacy), pages to keep out of search
// (sign-in, the learner/provider/admin areas, free-trial links) and addresses
// that aren't pages at all (which should answer 404). Pure, so the app and the
// Cloudflare Worker (worker/index.ts) agree. Keep it in step with
// src/app/router.tsx (a test checks every route is covered).

export type RouteKind =
  | 'home'
  | 'store'
  | 'product'
  | 'provider'
  | 'privacy'
  | 'terms'
  | 'refunds'
  | 'about'
  /** The list of study guides. */
  | 'guides'
  | 'guide'
  | 'support'
  /** Store search results: useful to people, not to search engines. */
  | 'search'
  /** Sign-in, sign-up, password reset, unsubscribe. */
  | 'auth'
  /** The app itself: dashboards, exams, checkout, free-trial links. */
  | 'private'
  | 'unknown';

export interface RouteInfo {
  kind: RouteKind;
  /** The product or provider slug. */
  slug?: string;
}

const SLUG = /^[a-z0-9][a-z0-9-]{0,120}$/;

const AUTH_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password', '/unsubscribe']);

/** Top-level areas of the app. A path is inside one when it is that path or continues with a "/". */
const PRIVATE_AREAS = ['/dashboard', '/question-banks', '/practice', '/exam', '/results', '/progress', '/purchases', '/checkout', '/profile', '/plus', '/study-plan', '/provider', '/admin', '/preview', '/t'];

/** Remove one trailing slash (except for "/"). */
export function normalisePath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;
}

export function classifyPath(pathname: string): RouteInfo {
  const path = normalisePath(pathname);
  if (path === '/') return { kind: 'home' };
  if (path === '/store') return { kind: 'store' };
  if (path === '/store/search') return { kind: 'search' };
  if (path === '/privacy') return { kind: 'privacy' };
  if (path === '/support') return { kind: 'support' };
  if (path === '/terms') return { kind: 'terms' };
  if (path === '/refunds') return { kind: 'refunds' };
  if (path === '/about') return { kind: 'about' };
  if (path === '/guides') return { kind: 'guides' };
  const guide = /^\/guides\/([^/]+)$/.exec(path);
  if (guide) return SLUG.test(guide[1]) ? { kind: 'guide', slug: guide[1] } : { kind: 'unknown' };
  if (AUTH_PATHS.has(path)) return { kind: 'auth' };

  const provider = /^\/store\/providers\/([^/]+)$/.exec(path);
  if (provider) return SLUG.test(provider[1]) ? { kind: 'provider', slug: provider[1] } : { kind: 'unknown' };
  const product = /^\/store\/([^/]+)$/.exec(path);
  if (product) return SLUG.test(product[1]) ? { kind: 'product', slug: product[1] } : { kind: 'unknown' };

  if (PRIVATE_AREAS.some((a) => path === a || path.startsWith(`${a}/`))) return { kind: 'private' };
  return { kind: 'unknown' };
}

/** Pages search engines may list. */
export const isIndexableKind = (kind: RouteKind) => ['home', 'store', 'product', 'provider', 'privacy', 'support', 'terms', 'refunds', 'about', 'guides', 'guide'].includes(kind);

/** The fixed public pages (the ones that don't depend on data), for the sitemap. */
export const STATIC_PUBLIC_PATHS = ['/', '/store', '/guides', '/about', '/support', '/terms', '/refunds', '/privacy'] as const;

/** What robots.txt keeps crawlers away from. Pages that should merely stay out of results (sign-in) are marked noindex instead. */
export const ROBOTS_DISALLOW = [
  '/dashboard',
  '/question-banks',
  '/practice',
  '/exam',
  '/results',
  '/progress',
  '/purchases',
  '/checkout',
  '/profile',
  '/plus',
  '/study-plan',
  '/provider',
  '/admin',
  '/preview',
  '/t/',
  '/store/search',
] as const;
